import {
  ChangeDetectionStrategy,
  Component,
  booleanAttribute,
  computed,
  contentChild,
  inject,
  input,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { HlmBottomSheet, type BottomSheetState, type BottomSheetTier } from '@lite-workspace/shared/helm-ui/bottom-sheet';
import { motionDurationMs } from '@lite-workspace/shared/helm-ui/utils';
import { IS_MOBILE, visualViewport } from '@lite-workspace/utils';

/**
 * Approved import-dialog chrome (fks-9417 / seamless import): the ONE container class string every
 * wizard-lane dialog puts on its host — only the `md:w-[var(--dlg-w-*)]` preset is added per dialog.
 * No close × belongs inside it: Esc, the backdrop and the footer buttons are the way out.
 * The chrome follows `dlg-phone`/`dlg-desktop` — the same device split as IS_MOBILE — so a phone held
 * sideways gets the sheet, not a desktop card wider than the screen.
 */
export const IMPORT_DIALOG_HOST =
  'block dlg-phone:contents dlg-desktop:rounded-[var(--r-xl)] dlg-desktop:border dlg-desktop:border-line2 ' +
  'dlg-desktop:bg-panel dlg-desktop:p-9 dlg-desktop:shadow-[var(--shadow-4)]';

/**
 * Hybrid dialog body: bottom-drawer on phones, plain projection inside the card on desktop.
 * The host dialog keeps its desktop chrome under `dlg-desktop:` and adds `dlg-phone:contents`.
 * Contract: ../CONTRACT.md — footprint stability is declared by the dialog's own root wrapper inside
 * the body (`min-h-*`): the loading and resolved states fill one container whose outer bounds never move.
 */
@Component({
  selector: 'hlm-dialog-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, HlmBottomSheet],
  host: { '[class]': 'hostClass()' },
  template: `
    @if (isMobile()) {
      <hlm-bottom-sheet
        [initialState]="tier()"
        [tiers]="ladder()"
        [backdrop]="false"
        [dismissible]="dismissible()"
        (stateChange)="onSheetState($event)"
      >
        <div [class]="sheetBodyClass()" [style.height]="screenHeight()" [style.margin-bottom.px]="keyboardInset()">
          @if (titled() && screenTitle()) {
            <div class="mb-4 grid shrink-0 grid-cols-[var(--layout-portal-header)] items-center gap-2">
              <button type="button" class="-ml-2 h-11 justify-self-start px-2 text-base text-acc" (click)="cancel()">
                Отмена
              </button>
              <h2 class="truncate text-base font-bold text-ink">{{ screenTitle() }}</h2>
            </div>
          }
          <ng-container [ngTemplateOutlet]="body()" />
        </div>
      </hlm-bottom-sheet>
    } @else if (stack()) {
      <div class="flex min-h-0 flex-1 flex-col">
        <ng-container [ngTemplateOutlet]="body()" />
      </div>
    } @else {
      <ng-container [ngTemplateOutlet]="body()" />
    }
  `,
})
export class DialogSheetComponent {
  protected readonly isMobile = inject(IS_MOBILE);
  private readonly dialogRef = inject(BrnDialogRef, { optional: true });
  protected readonly body = contentChild.required(TemplateRef);

  /**
   * One flex column from the desktop host down to the body (`flex-1 min-h-0`), phone sheet body
   * included: the dialog's root wrapper can fill the card or scroll inside its cap. The wrapper
   * itself carries the gap, scroll and `min-h-*` footprint.
   */
  readonly stack = input(false, { transform: booleanAttribute });

  /**
   * The card has no padding: its content runs to the card edge and the card clips (`overflow-hidden`).
   * The desktop scroller then takes no ring slack — there is no padding to lend, and the slack would
   * push its scrollbar past the card clip.
   */
  readonly flush = input(false, { transform: booleanAttribute });

  /**
   * The phone sheet closes the dialog through `close()`, which ignores `disableClose` — a dialog
   * that must outlive a gesture mid-send (a request in flight) turns this off for the duration.
   */
  readonly dismissible = input(true);

  /**
   * The desktop box on a SHORT viewport (a small desktop window; a phone held sideways before
   * IS_MOBILE learned it): the body caps at the viewport and scrolls inside, so the footer stays
   * reachable instead of hanging below the screen with nothing to scroll. The phone sheet carries
   * its own height cap, so it takes nothing from here. The scroller clips at its edge, so `-m-1 p-1`
   * gives it 4px of slack into the card padding: a focus ring or a sub-pixel button flush with the
   * body edge paints whole, while the body itself stays where it was. A `flush` card has no padding
   * to lend, so its scroller takes none. Scroll is vertical only: `overflow-y-auto` alone turns X into
   * `auto` too, and then any overflow (a full-bleed child, a sub-pixel edge) lets a text drag or a
   * sideways swipe slide the body.
   */
  protected readonly hostClass = computed(() => {
    if (this.isMobile()) return '';
    const display = this.stack() ? 'flex min-h-0 flex-col' : 'block';
    const slack = this.flush() ? '' : ' -m-1 p-1';
    return `${display}${slack} max-h-[var(--max-h-dialog-viewport)] overflow-x-hidden overflow-y-auto overscroll-contain`;
  });

  /**
   * Phone height tier. `screen` = full-screen sheet with the **Отмена** header and a keyboard-safe body;
   * `up` = the same header and body at two thirds of the viewport (the editor drawer's own height).
   */
  readonly tier = input<Extract<BottomSheetTier, 'up' | 'full' | 'screen'>>('full');

  /** Title of the `screen`/`up` header row: **Отмена** left · title · empty. Empty = no header. */
  readonly screenTitle = input<string>('');

  protected readonly screen = computed(() => this.tier() === 'screen');
  /** Tiers that carry the header row, a fixed-height body and the keyboard inset. */
  protected readonly titled = computed(() => this.tier() !== 'full');
  protected readonly ladder = computed<BottomSheetTier[]>(() => [this.tier()]);

  /** Visual viewport: `.height` is the free area above the keyboard, `.keyboard` the inset the body rides above. */
  private readonly viewport = visualViewport();

  // ponytail: the sheet chrome above the body (drag handle + its padding) is a constant 33px in
  // hlm-bottom-sheet; a measured offset would feed back into the height it sets. Read it live if the handle changes.
  protected readonly screenHeight = computed(() => {
    const viewport = this.viewport().height;
    if (!this.titled()) return null;
    // `up` fills its tier so the footer button sits at the sheet's bottom edge, not under the fields.
    if (!this.screen()) return 'calc(var(--sheet-h-up) - 33px)';
    return `calc(${viewport === null ? '100dvh' : `${viewport}px`} - 33px - env(safe-area-inset-top))`;
  });

  protected readonly keyboardInset = computed(() => (this.titled() ? this.viewport().keyboard : 0));

  protected readonly sheetBodyClass = computed(() => {
    if (this.stack()) return 'flex min-h-0 flex-1 flex-col px-1 pb-2 pt-1';
    return this.titled() ? 'flex flex-col px-1 pb-2 pt-1' : 'px-1 pb-2 pt-1';
  });

  protected cancel(): void {
    this.dialogRef?.close(undefined);
  }

  protected onSheetState(state: BottomSheetState): void {
    if (state === 'collapsed') setTimeout(() => this.dialogRef?.close(undefined), motionDurationMs('--t-mid'));
  }
}
